import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ExternalLink,
  CreditCard,
  AlertOctagon,
  Loader2,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { fetchStripeAccountId } from "@/services/transactions/stripeAccountService";

import { useStripeStatus } from "@/hooks/useStripeStatus";
import { useStripeConnection } from "@/hooks/useStripeConnection";

// Loading state for transactions
export const TransactionLoadingState: React.FC<{ message?: string }> = ({
  message = "Loading transactions...",
}) => (
  <Card className="p-10 text-center">
    <div className="space-y-4 flex flex-col items-center justify-center">
      <div className="animate-pulse">
        <CreditCard className="h-10 w-10 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-medium">{message}</h3>
      <p className="text-muted-foreground max-w-md mx-auto">
        We're fetching your transaction data from Stripe. This might take a
        moment.
      </p>
    </div>
  </Card>
);

// Error state for transactions
export const TransactionErrorState: React.FC<{
  error: string;
  onRetry: () => void;
}> = ({ error, onRetry }) => (
  <Card className="p-10 text-center">
    <div className="space-y-4 flex flex-col items-center justify-center">
      <AlertOctagon className="h-10 w-10 text-destructive" />
      <h3 className="text-lg font-medium">Error Loading Transactions</h3>
      <p className="text-muted-foreground max-w-md mx-auto">
        {error || "We encountered an error while loading your transactions."}
      </p>
      <div className="flex space-x-2">
        <Button onClick={onRetry} variant="default">
          Try Again
        </Button>
        <Link to="/settings?tab=stripe-connect">
          <Button variant="outline">Check Connection Settings</Button>
        </Link>
      </div>
    </div>
  </Card>
);

interface EmptyStateProps {
  reason?: "no-account" | "no-data" | "no-filtered-data";
  forceAccountCheck?: boolean;
}

export const TransactionEmptyState: React.FC<EmptyStateProps> = ({
  reason: initialReason = "no-data",
  forceAccountCheck = false,
}) => {
  const { isConnected, isConnecting, handleConnect, isLoading } =
    useStripeConnection();

  const [actualReason, setActualReason] = useState<
    "no-account" | "no-data" | "no-filtered-data" | "loading"
  >(forceAccountCheck ? "loading" : initialReason);

  // Only run this effect if we're doing a forced account check
  useEffect(() => {
    if (forceAccountCheck) {
      const checkStripeAccount = async () => {
        try {
          const accountId = await fetchStripeAccountId();
          console.log("has account", accountId);
          setActualReason(accountId ? "no-data" : "no-account");
        } catch (err) {
          console.error("Error checking Stripe account:", err);
          // Default to showing no data if we can't determine account status
          setActualReason("no-data");
        }
      };

      checkStripeAccount();
    } else {
      setActualReason(initialReason);
    }
  }, [forceAccountCheck, initialReason]);

  if (actualReason === "loading") {
    return <TransactionLoadingState message="Checking account status..." />;
  }

  if (actualReason === "no-account") {
    return (
      <Card className="p-10 text-center">
        {isLoading ? (
          <div className="text-center py-12 bg-card">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <div className="space-y-4 flex flex-col items-center justify-center">
            <h3 className="text-xl font-semibold mb-2 text-foreground">
              Connect your Stripe account
            </h3>
            <p className="text-muted-foreground max-w-md ">
              You need to connect your Stripe account to view your transactions.
            </p>
            {!isConnected && (
              <div className="w-full md:w-1/2">
                <Button
                  className="w-auto gradient-bg"
                  onClick={handleConnect}
                  disabled={isConnecting}
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      Connect Stripe Account
                      <ArrowUpRight className="h-12 w-12 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    );
  }

  // No data after filtering
  if (actualReason === "no-filtered-data") {
    return (
      <div className="py-10 px-10 text-center">
        <div className="space-y-4 flex flex-col items-center justify-center">
          <CreditCard className="h-10 w-10 text-muted-foreground" />
          <h3 className="text-lg font-medium">No Matching Transactions</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            We couldn't find any transactions matching your current filters. Try
            adjusting your filters to see more transactions.
          </p>
        </div>
      </div>
    );
  }

  // No transactions at all
  return (
    <Card className="p-10 text-center">
      <div className="space-y-4 flex flex-col items-center justify-center">
        <CreditCard className="h-10 w-10 text-muted-foreground" />
        <h3 className="text-lg font-medium">No Transactions Yet</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          You don't have any transactions yet. Create a product and start
          accepting payments to see them here.
        </p>
      </div>
    </Card>
  );
};